import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { BsArrowRight, BsTrash3Fill } from "react-icons/bs";
import Button from "../../components/Button";
import MovieCard from "../../components/MovieCard";
import MovieModal from "../../components/MovieModal";
import SearchBar from "../../components/SearchBar";
import SectionMessage from "../../components/SectionMessage";
import useFavorites from "../../hooks/useFavorites";

const Page = styled.main`
  display: grid;
  gap: 2rem;
  padding: 7rem 0 4rem;
`;

const Header = styled.section`
  display: grid;
  gap: 1rem;
  width: min(1280px, calc(100% - 1.5rem));
  margin: 0 auto;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(2rem, 4vw, 3.2rem);
`;

const Copy = styled.p`
  margin: 0;
  max-width: 70ch;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.75;
`;

const Stats = styled.div`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.9rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Grid = styled.section`
  display: grid;
  gap: 1rem;
  width: min(1280px, calc(100% - 1.5rem));
  margin: 0 auto;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 280px));
  justify-content: center;
  align-items: start;
`;

const CardShell = styled.article`
  display: grid;
  gap: 0.9rem;
  width: 100%;
`;

const CardFooter = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

function Favoritos() {
  const navigate = useNavigate();
  const { favorites, removeFavorite } = useFavorites();
  const [query, setQuery] = useState("");
  const [selectedMovieId, setSelectedMovieId] = useState(null);

  const filteredFavorites = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return favorites;
    }

    return favorites.filter((movie) => {
      const genreText = movie.genreNames?.join(" ").toLowerCase() || "";
      return movie.title.toLowerCase().includes(normalizedQuery) || genreText.includes(normalizedQuery);
    });
  }, [favorites, query]);

  return (
    <Page>
      <Header>
        <Stats>{favorites.length} titulos salvos</Stats>
        <Title>Minha Lista</Title>
        <Copy>
          Tudo que voce marcou para rever, compartilhar ou assistir depois fica aqui com acesso rapido ao detalhe completo.
        </Copy>
        <SearchBar value={query} onChange={setQuery} onClear={() => setQuery("")} />
      </Header>

      {!favorites.length ? (
        <SectionMessage
          title="Sua lista ainda esta vazia"
          description="Explore o catalogo principal, abra um filme e salve os titulos que merecem um lugar na sua watchlist."
          actionLabel="Ir para a home"
          onAction={() => navigate("/")}
        />
      ) : null}

      {favorites.length > 0 && !filteredFavorites.length ? (
        <SectionMessage
          title="Nenhum favorito combina com essa busca"
          description="Limpe o campo ou tente procurar por outro termo para reencontrar seus titulos salvos."
          actionLabel="Limpar busca"
          onAction={() => setQuery("")}
        />
      ) : null}

      {filteredFavorites.length ? (
        <Grid>
          {filteredFavorites.map((movie) => (
            <CardShell key={movie.id}>
              <MovieCard movie={movie} onSelect={setSelectedMovieId} />
              <CardFooter>
                <Button as={Link} to={`/filme/${movie.id}`} variant="secondary" icon={<BsArrowRight />} fullWidth>
                  Ver pagina
                </Button>
                <Button variant="danger" icon={<BsTrash3Fill />} onClick={() => removeFavorite(movie.id)} fullWidth>
                  Remover
                </Button>
              </CardFooter>
            </CardShell>
          ))}
        </Grid>
      ) : null}

      <MovieModal
        movieId={selectedMovieId}
        genresById={{}}
        onClose={() => setSelectedMovieId(null)}
        onSelectMovie={setSelectedMovieId}
      />
    </Page>
  );
}

export default Favoritos;
